import type { QueuedWrite, SendOutcome, Transport } from './types.js'

/**
 * The one place in the counter that talks to the API. §6.22 `OFF-001a`, §9.4 `ARC-006`.
 *
 * `OFF-001a` `MUST`: "All writes go through the queue abstraction from day one, **with no direct
 * API calls from the UI**." So `fetch` appears here and nowhere else, and a lint rule in
 * `packages/config` makes that a build failure rather than a convention. A convention lasts until
 * the first hurried afternoon.
 */

export interface TransportOptions {
  readonly baseUrl: string
  /** The tenant is resolved once and carried explicitly. Never a global, never an ambient default. */
  readonly tenantId: string
  readonly accessToken: () => string | undefined
  /** Injected so a test can drive it, and so an offline retry does not depend on a real socket. */
  readonly fetch: typeof globalThis.fetch
}

/**
 * Statuses that mean "do not retry, ask a human".
 *
 * 409 is here because a version conflict is `CLAUDE.md`'s optimistic locking doing its job: "A
 * stale update returns a conflict with the current state, never a silent overwrite." Retrying would
 * either fail forever or, worse, succeed against a row that has since changed.
 *
 * 413 is here because a body that is too large is too large every time. It was absent and fell
 * through to `unreachable`, which retries with backoff forever at the head of a serial queue, and
 * until Mira's vision tasks nothing the counter sent came near a size limit: four cropped
 * photographs are about 8 MB of base64. Fastify's own `bodyLimit` is 20 MB here, but nginx's default
 * `client_max_body_size` is 1 MB and `lensmira-infra` is empty, so the first deployment behind a
 * proxy would have turned every vision ask into an endless re-upload with every sale queued behind
 * it. `anthropic-provider.ts` already classifies 413 as permanent on its own side.
 *
 * 5xx and a thrown fetch are the opposite: unreachable, retry with backoff.
 */
const REFUSED_STATUSES = new Set([400, 401, 403, 404, 409, 413, 422])

export function httpTransport(options: TransportOptions): Transport {
  return async (write: QueuedWrite): Promise<SendOutcome> => {
    const token = options.accessToken()
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      // decisions/0005: the header is Idempotency-Key and the value is the UUIDv7 minted when the
      // write was enqueued. Unchanged across every retry, which is the whole of ARC-006.
      'Idempotency-Key': write.idempotencyKey,
      'X-Lensmira-Tenant': options.tenantId,
    }
    if (token) headers['authorization'] = `Bearer ${token}`
    // No `If-Match`. It was sent here and read nowhere, which made a write look locked when it
    // was not. `QueuedWrite` says where the lock goes instead.

    let response: Response
    try {
      response = await options.fetch(`${options.baseUrl}${write.path}`, {
        method: write.method,
        headers,
        body: JSON.stringify(write.body),
      })
    } catch (error) {
      // The thirty-second outage OFF-001 is built for. Nothing is lost; the backoff tries again.
      return { kind: 'unreachable', message: error instanceof Error ? error.message : 'network' }
    }

    if (response.ok) {
      // The body is carried so the screen that made this write can read its own result. A body that
      // does not parse is not a failed send: the server accepted it either way.
      const accepted = (await response.json().catch(() => undefined)) as unknown
      return { kind: 'accepted', body: accepted }
    }

    if (REFUSED_STATUSES.has(response.status)) {
      const body = (await response.json().catch(() => ({}))) as {
        code?: string
        message?: string
        details?: unknown
      }

      /**
       * **`IDEMPOTENCY_IN_FLIGHT` is the one 409 that means "come back", not "no".**
       *
       * The server answers it with "That request is still being processed. Try again in a moment",
       * and 409 sits in `REFUSED_STATUSES`, so the queue marked the write permanently refused and
       * showed the shop a failure. The only client that can ever see that message was the one
       * guaranteed never to act on it: the two halves of `ARC-006` contradicted each other, and the
       * write it happened to was one the server had accepted and was busy completing.
       *
       * Classified as unreachable instead, which is the queue's retry path, because that is
       * precisely what it is: the answer is not ready yet.
       */
      if (body.code === 'IDEMPOTENCY_IN_FLIGHT') {
        return { kind: 'unreachable', message: body.message ?? 'Still processing, will retry.' }
      }

      return {
        kind: 'refused',
        // Appendix F codes travel to the UI unchanged, so the message a shop sees is the one the
        // error catalogue wrote rather than one invented at the call site.
        code: body.code ?? `HTTP_${response.status}`,
        message: body.message ?? response.statusText,
        /**
         * **And so does `details`, which was being dropped here.**
         *
         * 81 call sites in `apps/api` answer a conflict through `rethrowAsHttp(error,
         * conflictDetails)`, which puts the current state of the record on the response exactly as
         * Appendix J requires. This function read `code` and `message` and threw the rest away, so
         * the one thing a shopkeeper needs in order to resolve a version conflict never reached the
         * screen: `BlockedWrites` could offer Try again, which re-sends the `expectedVersion` that
         * already lost the race, or Discard, which loses their own work. The server had been
         * sending the answer all along.
         */
        ...(body.details === undefined ? {} : { details: body.details }),
      }
    }

    return { kind: 'unreachable', message: `HTTP ${response.status}` }
  }
}
